import { Typography } from '@festina-life/ui/structure'
import { useEffect, useMemo } from 'react'

import { useHook, useService } from '../../lenteHooks'
import { withLifeErrorBoundary } from '../../libs/withLifeErrorBoundary'

function CommitmentNavigationItemInternal({
  tenantId,
  ownerId,
  onRenderFinished,
}: {
  tenantId: string
  ownerId: string
  onRenderFinished?: (textContent: string) => void
}) {
  const { shouldShowName, shouldShowInternalId, shouldShowExternalId } =
    useHook('general.search.useRecentSearchDisplaySettings')()

  const formatFullName = useService('formatter.personEntityNameFormatter')
  const socialSecurityNumberFormatter = useService(
    'formatter.socialSecurityNumberFormatter',
  )

  const { data: externalId } = useHook('fetchers.crm.useFetchExternalId')(
    {
      tenantId,
      internalId: ownerId,
    },
    { enabled: shouldShowExternalId },
  )
  const { data: personVersion } = useHook(
    'fetchers.crm.useFetchPersonVersionLatest',
  )(
    {
      tenantId,
      personId: ownerId,
    },
    { enabled: shouldShowName },
  )

  const textContent = useMemo(() => {
    const name = shouldShowName
      ? formatFullName(personVersion?.personEntityNameVersion, '')
      : ''

    let ssnFormatted = shouldShowExternalId
      ? socialSecurityNumberFormatter(externalId)
      : ''

    if (shouldShowInternalId) {
      // For consistency, internal ID is always paranthesized if external ID should be visible.
      ssnFormatted += shouldShowExternalId ? ` (${ownerId})` : ownerId
    }

    return [name, ssnFormatted].filter(Boolean)
  }, [
    externalId,
    formatFullName,
    personVersion?.personEntityNameVersion,
    ownerId,
    shouldShowExternalId,
    shouldShowInternalId,
    shouldShowName,
    socialSecurityNumberFormatter,
  ])

  useEffect(
    () => onRenderFinished?.(textContent.join(', ')),
    [onRenderFinished, textContent],
  )

  return (
    <>
      {textContent.map((text, index) => (
        <Typography key={text} intensity={index === 0 ? 'primary' : 'tertiary'}>
          {text}
        </Typography>
      ))}
    </>
  )
}

export const CommitmentNavigationItem = withLifeErrorBoundary(
  CommitmentNavigationItemInternal,
)
